// Site-wide amber "Demo Data" ribbon per ADR-0002.
//
// Render depends on the current course's `simulate_sensors` feature flag.
// Sits at the top of the app shell so every page makes the synthetic-data
// status unmissable. Swallows errors silently — missing ribbon is better
// than a crashed app when the sensors API 500s briefly.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSensorsFeatureFlag } from '../api/sensors.js';

export function DemoDataRibbon() {
  const { courseSlug } = useParams();
  const [simulate, setSimulate] = useState<boolean>(false);

  useEffect(() => {
    if (!courseSlug) {
      setSimulate(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await getSensorsFeatureFlag(courseSlug);
        if (!cancelled) setSimulate(r.simulateSensors);
      } catch {
        if (!cancelled) setSimulate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (!simulate) return null;

  return (
    <div
      className="relative overflow-hidden border-b text-xs md:text-sm"
      style={{
        backgroundColor: 'var(--warning-muted)',
        borderBottomColor: 'rgba(245, 166, 35, 0.30)',
        color: 'var(--color-warning)',
      }}
    >
      {/* Shimmer edge */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-24 animate-[demoShimmer_6s_ease-in-out_infinite]"
        style={{
          background:
            'linear-gradient(90deg, rgba(245, 166, 35, 0.35) 0%, rgba(245, 166, 35, 0.05) 60%, transparent 100%)',
        }}
      />
      <div className="relative flex flex-wrap items-center gap-2 px-4 py-1.5 md:px-6">
        <span className="font-semibold uppercase tracking-[0.12em]">Demo data</span>
        <span className="text-fg-secondary">
          Sensor readings are physics-simulated from real weather until Soil Scout hardware deploys.
        </span>
        <span
          className="ml-auto rounded-pill px-2 py-0.5 text-[10px] uppercase tracking-wider"
          style={{ backgroundColor: 'rgba(245, 166, 35, 0.18)' }}
        >
          ADR-0002
        </span>
      </div>
      <style>{`
        @keyframes demoShimmer {
          0%, 100% { transform: translateX(-30%); opacity: 0.6; }
          50% { transform: translateX(120%); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
